import { sessionDateOf } from './replayStats';
import { priceTrade, normaliseAccount } from './accountCosts';

/**
 * A SIMULATOR SESSION: the window, what is drawn, and which engine is being traded against.
 *
 * <h2>Why a session is not the same thing as a run</h2>
 *
 * A RUN is everything the engine did — on SPY daily that is 8,466 bars from 1993, and the engine
 * needs all of it, because a 200-period macro average cannot be computed from the middle of a
 * series. A SESSION is the stretch an operator actually sits through.
 *
 * <p>So the window is applied here rather than by asking the backend for fewer bars: the engine
 * still sees its whole history and makes the same decisions, and what changes is which bars the
 * transport will walk and which trades the comparison counts.
 *
 * <h2>THE COMPARISON IS BOUNDED TO THE SESSION, and that is the point of the window</h2>
 *
 * The single-book Running Ledger keeps its existing meaning — the engine's book up to this bar,
 * cumulative from the start of the run — because that is a documented, tested quantity and it is
 * the right answer to "what has this strategy done".
 *
 * <p>The ALGO vs HUMAN comparison is different. The operator can only trade inside the session, so
 * scoring their four trades against the engine's thirty years is not a comparison of anything. A
 * session that opened "100 trades closed" before a single click was the reason the replay fetches
 * every bar in the first place; bounding the comparison is what makes a chosen window usable.
 */

/** The three engines a session can be traded against. One mandate at a time, by design. */
export const MANDATES = [
  {
    value: 'PTA',
    label: 'PTA Only',
    hint: 'The equator engine: a close crossing the midline between the Top and Bottom lines, '
      + 'with the perimeter as a runaway fallback. Daily SPY: 185 trades, PF 1.18 — and the '
      + 'entries measured NEUTRAL in the forward-return test, not predictive.',
  },
  {
    value: 'PTB',
    label: 'PTB Only',
    hint: 'The eight micro-crossing rules on the second band timeline. Daily SPY with the macro '
      + 'gate: 252 trades, PF 1.49, net +89.73% — the best band result measured, and still one '
      + 'symbol with no measured directional edge.',
  },
  {
    value: 'CTS',
    label: 'CTS Only',
    hint: 'Critical Timing Scan dates, with direction from the 5-bar average — contrarian, so a '
      + 'close below buys. Daily SPY: 258 trades, PF 1.52, net +26.36%.',
  },
];

export const MANDATE_VALUES = MANDATES.map((m) => m.value);

/**
 * WHAT THE FEATURE IS CALLED ON SCREEN.
 *
 * <p>Declared once rather than typed into a title, a button, a tooltip and an aria-label. A
 * product name is the thing most likely to be changed later and least likely to be changed
 * everywhere -- the same arithmetic that put `#d4a017` in three files under three comments each
 * claiming it was shared.
 *
 * <p>It names the SESSION workbench and nothing else. Two neighbouring surfaces are deliberately
 * untouched: the DynaMan research dialog, which is a separate admin tool, and the `toggle_simulate`
 * reveal mode, renamed **Alpha Replay** on 2026-10-20 and now the icon immediately before them.
 * Three controls in one group, three names — calling them all the workbench, or all Alpha
 * something, would make them indistinguishable exactly where they sit side by side.
 *
 * <p>⚠ Alpha Replay and the Verifiable Alpha WORKBENCH are different things and now share a word.
 * The Replay hides the future on the ordinary chart and takes no trades; the Workbench runs a
 * session in which a reader trades against the engine. A figure quoted from one is not a figure
 * from the other.
 */
export const WORKBENCH_NAME = 'Verifiable Alpha Workbench';

/** The launch action. Short, because MUI upper-cases it into a button of fixed width. */
export const WORKBENCH_LAUNCH = 'Launch Workbench';

/** What the setup modal offers, and what a session draws by default. */
export const STRUCTURAL_CATEGORIES = ['PTA', 'PTB', 'CTS'];

/**
 * ALL THREE drawn.
 *
 * <p>Both band systems because the mandate is PTB and PTA is the structure it is read against;
 * CTS because its timing points are the third thing a reader checks an entry against, and a
 * canvas that shows two of the three invites the question about the missing one.
 *
 * <p>The cost of the two it is not trading is one extra request each: `maxBars: 1` returns the
 * whole band table in a few hundred bytes, because `timeBands` is written before the replay
 * window is chosen. CTS costs nothing at all -- those points come from the Critical Timing Scan
 * already on the chart. See `buildBandOnlyRequest`.
 *
 * <p>It is DENSE: CTS marks several hundred sessions over a multi-decade axis. A reader who finds
 * it too busy unticks one box, which is the cheaper mistake to make in this direction.
 */
export const DEFAULT_OVERLAYS = Object.freeze({ PTA: true, PTB: true, CTS: true });

/**
 * PTB, which is the better-measured of the two band engines.
 *
 * <p>On daily SPY with the macro gate on -- which a session turns on, from `PROVIDER_BASELINES` --
 * PTB is 252 trades at +197.66% gross and PF 1.49, net +89.73%, against PTA's 142 / +71.65% /
 * 1.48. Both are one symbol and one orb, and the forward-return test found no directional edge in
 * either, so this is "the better default of two" rather than a recommendation.
 */
export const DEFAULT_MANDATE = 'PTB';

/**
 * HOW THE PLAYHEAD ADVANCES.
 *
 * <pre>
 *   AUTO     the speed slider drives `useReplayClock`, as it always has
 *   MANUAL   the timer is never started. One bar per click, or per right-arrow
 * </pre>
 *
 * <p>Two modes rather than "set the speed to zero", which is not expressible — `clampDelay` has a
 * 1ms floor precisely so a zero cannot become a busy loop that locks the tab. And a mode is the
 * honest shape for this: in MANUAL the play button is not slow, it is absent, and a control that
 * looked available and did nothing would be worse than one that is visibly not offered.
 *
 * <p>AUTO is the default, because that is what the transport has always done and a reader who
 * launches without reading the modal should get the behaviour the slider implies.
 */
export const PROGRESSION = Object.freeze({ AUTO: 'AUTO', MANUAL: 'MANUAL' });

export const PROGRESSION_MODES = [
  {
    value: PROGRESSION.AUTO,
    label: 'Auto-Timer',
    hint: 'The speed slider advances the bars. 50ms to 3s per bar, adjustable while it runs.',
  },
  {
    value: PROGRESSION.MANUAL,
    label: 'Manual Click',
    hint: 'No timer at all. One bar per Next Bar click or right-arrow press — which is the mode '
      + 'for actually deciding on each bar rather than watching bars go past.',
  },
];

export const DEFAULT_PROGRESSION = PROGRESSION.AUTO;

/**
 * WHERE THE BARS COME FROM.
 *
 * <pre>
 *   INTERNAL   a symbol from the backend's own catalogue, typed with autocomplete
 *   EXTERNAL   a CSV the operator uploads, parsed in the browser
 * </pre>
 *
 * <p>An explicit choice rather than one option buried at the top of an eight-hundred-row
 * dropdown. The two modes need different controls and ask different questions — one validates a
 * ticker, the other asks whether the bars may be sent anywhere — and a single list that silently
 * changes the rest of the dialog when one of its rows is picked is a mode pretending not to be
 * one.
 */
export const DATA_SOURCE = Object.freeze({ INTERNAL: 'INTERNAL', EXTERNAL: 'EXTERNAL' });

export const DATA_SOURCES = [
  {
    value: DATA_SOURCE.INTERNAL,
    label: 'Select from list (Internal)',
    hint: 'A symbol the platform already holds. Type to search; the engine runs on it.',
  },
  {
    value: DATA_SOURCE.EXTERNAL,
    label: 'External (CSV Upload)',
    hint: 'Your own OHLCV file, parsed in this browser. You choose whether the bars are sent to '
      + 'the engine at all.',
  },
];

/**
 * THE TRADE-MANAGEMENT LEVERS a session may switch on, and their OFF state.
 *
 * <h2>Off is the control, and the control is what every published figure was measured under</h2>
 *
 * All four are Phase 6 mechanics and all four default off in the engine itself, so a session that
 * leaves this section alone runs the measured baseline. That is not a convention a reader has to
 * trust: `PTA`, `PTB` and `CTS` were verified byte-identical with every one of these levers
 * compiled in, by diffing ledger CSVs against a git worktree at the parent commit.
 *
 * <h2>What they measured, so the dialog can say</h2>
 *
 * On PTA@SPY daily, only the time stop is better than doing nothing: 185 trades / +43.89% / PF
 * 1.25 against the control's +28.65% / 1.18. The fan costs 32 points of gross and the ATR trail
 * 20 — both lift the win rate and shorten the hold, which is them cutting trades these entries
 * could not afford to have cut. The hints carry those numbers, because a control offered with no
 * figure beside it reads as an improvement nobody has checked.
 */
export const TRADE_MANAGEMENT = Object.freeze({
  FAN_EXIT: 'fanExit',
  FAN_RELOAD: 'fanReload',
  ATR_TRAILING_STOP: 'atrTrailingStop',
  MAX_BARS_IN_TRADE: 'maxBarsInTrade',
});

/**
 * THE TUNING PARAMETERS: how SENSITIVE two of those mechanics are, rather than whether they run.
 *
 * <h2>Parameters, not loosened rules — which is the whole point of them</h2>
 *
 * The reload fires rarely, and the cheap response would have been to widen the band in the Java
 * default and call the mechanic fixed. That would have moved a measured figure for every caller
 * including the CLI, with no way to get back to it. These are the same three numbers exposed, so
 * a reader changes sensitivity for THEIR run and every published figure still reproduces from a
 * bare command.
 *
 * <h2>What they bind on, measured on PTB@SPY daily</h2>
 *
 * <pre>
 *   band 0.382-0.618 (shipped)    8 reloads out of 85 fan breaks
 *   band 0.300-0.700              9
 *   band 0.200-0.800             14     <- the band binds harder than the EMA
 *   EMA 3 (shipped is 8)         10     <- the confirmation is second
 *   watch 60 bars (shipped 20)    8     <- the window does not bind at all
 *   fan pivot width 3 / 4 / 5   4 / 1 / 0, on 51 / 31 / 20 fan breaks
 * </pre>
 *
 * <p>The FAN WIDTH is the strongest of the four and it works upstream of the other three: it
 * decides how many fan breaks exist to arm a watch at all, so at width 5 there are 20 breaks and
 * no reloads whatever the band is set to.
 *
 * <p>So the watch window is deliberately NOT offered. A control whose own sweep shows it changes
 * nothing is a control a reader will spend time on, and the measurement says where to spend it.
 *
 * <p>Read the RETURNS from that sweep with the usual caution: width 5 gives PTB +40.33% gross at
 * PF 1.15 against the shipped width's +2.13% at 1.05, and the four points are non-monotone
 * (+2.13 / -10.69 / +9.17 / +40.33). That is the best row of a four-point sweep on one symbol,
 * which is the overfit shape this repository keeps recording -- so nothing has been promoted off
 * it. It is also an echo of a finding already on record: band runs use trendline fractal 5
 * precisely because width 2 calls a single-bar spike a swing, and the fan was left at the
 * global 2.
 *
 * <p>The fan's fractal width is the fourth, and it is the one that decides how MACRO the fan's
 * anchors are: a wider fractal needs a bigger turn to count as a pivot, so the line re-slopes
 * less often and breaks later.
 */
export const TRADE_TUNING = Object.freeze({
  FAN_FRACTAL_WIDTH: 'fanFractalWidth',
  RELOAD_EMA: 'reloadEma',
  STRUCTURAL_TOLERANCE: 'structuralTolerance',
  SAME_BAR_ENTRY: 'sameBarEntry',
  CROSSOVER_REBOUND: 'crossoverRebound',
  MAX_TRANCHES: 'maxTranches',
  ENGINE_MODE: 'engineMode',
  DIRECT_BREAKEVEN_R: 'directBreakevenR',
  ENGINE_PRESET: 'enginePreset',
  CHARGED_CONTINUATION: 'chargedContinuation',
  CONTINUATION_TREND_SMA: 'continuationTrendSma',
});

/**
 * THE RETRACEMENT BAND IS GONE, and the two keys are listed here rather than deleted.
 *
 * <p>Phase 10 replaced the fractional band with a binary reload -- a fan exit inside a live
 * structural window arms the watch, a close back across the confirmation EMA fires it -- so
 * `reloadMinRetracement` and `reloadMaxRetracement` are no longer read by anything. The backend
 * REFUSES them with an explanation rather than ignoring them, because Spring drops an unknown
 * parameter in silence and a saved URL would otherwise run the new rule while its author believed
 * they had set a band.
 *
 * <p>So this list exists for the test that asserts the session never carries one. A removed field
 * leaves no trace to assert against, which is how a dead lever comes back through a merge.
 */
export const RETIRED_TUNING = Object.freeze(['reloadMinRetracement', 'reloadMaxRetracement']);

/**
 * EVERY MECHANIC OFF - the engine's own defaults, and what every published figure was measured
 * under.
 *
 * <p>This is what an ABSENT management block resolves to, and it must stay that way: the backend
 * derives its explicit-lever set from nullability, so a caller that omits the block is asking for
 * the engine's defaults rather than for this application's opinion. "Absent means absent" is the
 * rule every other lever in this codebase follows.
 */
/**
 * THE TWO EXECUTION LAYERS, declared once.
 *
 * <p>LEGACY first and default, because it is the frozen module: every figure in CLAUDE.md was
 * measured on it, and a dropdown whose first row silently changed what a run means would
 * invalidate all of them at once.
 */
export const ENGINE_MODES = Object.freeze([
  {
    value: 'LEGACY',
    label: 'Legacy — full state machine',
    hint: 'The engine every published figure was measured on: the macro regime gate, volume '
      + 'confluence, structural profit targets, the CTS exit sandbox, phase management, the '
      + 'accelerating fan and three other trailing mechanics, reversals, and four re-entry '
      + 'mechanics. Frozen — nothing in the Direct engine changes it.',
  },
  {
    value: 'DIRECT',
    label: 'Direct — pure geometry',
    hint: 'Unconditional PTA / PTB / CTS entries with NO macro filter and no regime veto, one '
      + 'initial stop, a mechanical move to breakeven, and one exit: a ratcheting trendline '
      + 'anchored at the trigger bar’s own low that may only ever get steeper. A trendline exit '
      + 'leaves a watermark, and a close back through it retakes the trade immediately. '
      + 'UNMEASURED — no figure in this repository comes from a run with it.',
  },
  {
    value: 'TIMING_ENGINE',
    label: 'Timing Engine',
    hint: 'THE VALIDATED CTS CONFIGURATION as one selection, with nothing left to configure: the '
      + 'Legacy state machine, the inflection vector ("look left" — rallied into the node is a '
      + 'top), the price-confirmation gate (the node ARMS, a later break of its extreme TRIGGERS), '
      + 'the exit sandbox and BOTH structural profit targets off, the CTS mandate and the DAILY '
      + 'bar set — all forced, so selecting it and pressing Run reproduces the figure with no '
      + 'second control to touch. SPY: 148 trades, +126.53% gross, PF 1.70, mean R 0.50, '
      + 'asymmetry 7.57:1 against an 11.7% break-even. Because it forces them it cannot be used '
      + 'to SWEEP them — pick Legacy and set the rules by hand, which reproduces anything this '
      + 'mode can. Three caveats travel with the number. The 18.2% win rate is the intended '
      + 'shape, not a fault: this is a small-stop, long-tail book and the asymmetry is where the '
      + 'return is. Forcing the profit targets off is worth +2.5 points of gross and 0.07 of PF '
      + 'over the 162-trade / +124.05% / PF 1.63 run that inherited them, and it costs '
      + 'drawdown — 25.14% currency against 13.19%. And the stop is anchored on the bar the '
      + 'order FILLED on, which the 2026-10-13 audit showed is tighter than the node under the '
      + 'gate; the structural anchor is one flag away on Legacy.',
  },
  {
    value: 'PTB_TIMING_ENGINE',
    label: 'PTB + Timing Engine',
    hint: 'THE CTS CALENDAR READ THROUGH PTB GEOMETRY. PTB’s own generation bands are the '
      + 'vertical time pillars — measured on the shipped dataset at a median 88-day cycle with '
      + '4 bands in 59 of 69 full years, which is the quarterly cadence — and its Top/Bottom '
      + 'lines are the channel. The calendar is graded into three conviction tiers by ORB '
      + '(1.0 tightest / 2.0 less tight / 3.0 liberal), which nest exactly. Tightest enters on '
      + 'a breakout, or on a reaction at a line INSIDE a pillar; less-tight adds a scale-in on '
      + 'a breakout only; liberal never enters and is audited as early warning or filtered as '
      + 'mid-channel noise. SPY: 63 trades, −9.53% gross, PF 0.98 — IT LOSES, and the per-tier '
      + 'split inverts the brief: the TIGHTEST tier is −24.67% at PF 0.27 while the merely '
      + 'secondary LESS_TIGHT scale-ins are +22.69% at PF 1.46. Raw CTS on the same bars is '
      + '+126.53% at PF 1.70, so this composition costs 136 points. Giving the scale-ins the '
      + 'capacity the brief implies (3 tranches) makes it worse again, at −35.26%.',
  },
]);

/**
 * THE MANDATE EACH ENGINE REQUIRES, or null where it accepts any.
 *
 * <p>The Timing Engine is the CTS system: its forced rules are read by the CTS provider alone, so
 * a Timing run on a PTA mandate would be an ordinary Legacy band run with every distinguishing
 * rule inert and the dropdown still reading "Timing Engine". The BACKEND refuses that combination
 * outright; this is what stops the dialog ever sending it, so the refusal is a guard against a
 * hand-built request rather than something a reader can trip over.
 */
export const ENGINE_REQUIRED_MANDATE = Object.freeze({
  TIMING_ENGINE: 'CTS',
  // Its own provider, which is the only one holding both the tiers and the PTB geometry. The
  // backend refuses any other, so sending CTS here would be a 400 rather than a run.
  PTB_TIMING_ENGINE: 'PTB_TIMING',
});

/**
 * THE CTS RULES AN ENGINE OWNS, written into the visible controls when it is selected.
 *
 * <h2>Materialised rather than left to the backend, which applies them either way</h2>
 *
 * `TIMING_OPTIMAL` forces these five on the server, so a run is correct whatever the dialog
 * shows. What would NOT be correct is the dialog: selecting the Timing Engine left the direction
 * dropdown reading "SMA pullback" over a run using the inflection vector — the screen saying one
 * thing and the ledger coming back from another, which CLAUDE.md records three times as this
 * repository's worst defect class and which this one shipped again here, found by reading the
 * dropdown in a browser.
 *
 * <p>So the values are written in AND the controls are disabled while the mode owns them: a
 * field a reader can edit and the engine then overrides is the same lie in a different costume.
 *
 * <p>Pinned against {@code EnginePreset.TIMING_OPTIMAL} by a test, because these are a SECOND
 * declaration of values the Java already holds and the two would otherwise drift.
 */
export const ENGINE_FORCED_CTS_RULES = Object.freeze({
  TIMING_ENGINE: Object.freeze({
    ctsDirection: 'INFLECTION_VECTOR',
    ctsVectorLookback: 3,
    ctsPriceConfirmation: true,
    stopStrategy: 'SIGNAL_BAR_EXTREME',
    ctsExitSandbox: false,
    // THE BAR SET IS PART OF THE MODE, not a convenience. The validated figure is a DAILY run
    // -- with intraday bars present the ATR trailing floor fires 13 times and 162/+124.05%
    // becomes 165/+99.26% under the same name -- and this dialog defaults to all stored series.
    // Pinned against `EnginePreset.forcedTimeframes` by a test.
    barSet: 'daily',
    // NO STRUCTURAL PROFIT TARGETS. `targetPtbEnabled` is true on the engine builder, so a
    // Timing run inherited it and 30 of 162 exits were Price Target B. Forcing it off moves the
    // figure to 148 / +126.53% / PF 1.70 -- better on gross and asymmetry, lower win rate.
    targetPta: false,
    targetPtb: false,
  }),
  /**
   * PTB + TIMING forces everything the Timing Engine does, and the band vocabularies OFF.
   *
   * <p>Spread from the entry above rather than retyped, so the two cannot drift -- the preset
   * delegates on the Java side for exactly the same reason. What is added is the half that
   * differs: this engine READS the PTB channel, so the band rules that would otherwise also
   * trade those lines are silenced. Two systems reading one set of lines with different rules
   * makes every row in the ledger unattributable.
   */
  PTB_TIMING_ENGINE: Object.freeze({
    ctsDirection: 'INFLECTION_VECTOR',
    ctsVectorLookback: 3,
    ctsPriceConfirmation: true,
    stopStrategy: 'SIGNAL_BAR_EXTREME',
    ctsExitSandbox: false,
    barSet: 'daily',
    targetPta: false,
    targetPtb: false,
  }),
});

/**
 * WHAT EACH ENGINE'S PRESET SETS THAT THIS DIALOG CAN SHOW.
 *
 * <h2>Why the dialog materialises the preset instead of letting the backend apply it silently</h2>
 *
 * The preset is applied on the backend -- it has to be, because two of its levers (the macro 50/200
 * gate and the breakeven stop) have no control here, and because a batch CLI run needs the same
 * environment. But the four below DO have controls, and a request that carried
 * `maxTranches: 1, fanFractalWidth: 2` while the run used 3 and 8 is the invisible divergence
 * CLAUDE.md records three times as this repository's worst defect class: the screen would say one
 * thing and the ledger would come back from another.
 *
 * <p>So selecting an engine WRITES these into the visible controls. The backend then applies the
 * same values over them, which costs nothing because the preset is idempotent, and supplies the two
 * this dialog cannot show -- named in the switch's own hint rather than left to be discovered.
 *
 * <p>Unticking the preset writes the CONTROL's values back, which is what keeps the measured
 * baseline reachable from this dialog: a preset that only ever wrote values would leave a reader
 * who switched it off running three tranches with a 17-bar fan and no way back.
 */
export const ENGINE_PRESET_LEVERS = Object.freeze({
  LEGACY: Object.freeze({
    maxTranches: 3,
    fanExit: true,
    // A 17-BAR PIVOT -- a macro fan. Wider than any point on the recorded width sweep.
    fanFractalWidth: 8,
    chargedContinuation: true,
  }),
  DIRECT: Object.freeze({
    maxTranches: 1,
    // STRICTLY off: the fan and the ratcheting line are two opinions about the same question and
    // the fan is the looser one, so it would pre-empt the exit this engine exists to test.
    fanExit: false,
    fanFractalWidth: 2,
    chargedContinuation: true,
  }),
  /**
   * PTB + TIMING runs in the Timing Engine's environment. One tranche, no fan, no continuation.
   *
   * <p>Its own entry rather than a fallback to LEGACY's: `applyEnginePreset` iterates the LEGACY
   * key list, so an absent entry writes `undefined` into all four controls -- the defect the
   * Timing entry was added for, which would arrive again for any mode added later.
   *
   * <p>ONE TRANCHE is also the measurement: the scale-in tier is the one thing capacity would
   * change, and three tranches took the run from -9.53% to -35.26%. The capacity the brief's
   * "scale-in" implies is reachable and is worse.
   */
  PTB_TIMING_ENGINE: Object.freeze({
    maxTranches: 1,
    fanExit: false,
    fanFractalWidth: 2,
    chargedContinuation: false,
  }),

  /**
   * THE TIMING ENGINE: the ENGINE DEFAULTS, which is the environment its figures were measured in.
   *
   * <h2>These four are NOT forced by the backend preset, and the distinction is the point</h2>
   *
   * {@code TIMING_OPTIMAL} forces five CTS RULES -- the direction rule, the vector lookback, the
   * gate, the stop strategy and the macro bypass -- and not one of them has a control in this
   * dialog. What it deliberately does NOT force is the book: capacity, the fan and the
   * continuation stay the caller's, because "which rules decide a trade" and "how many at once"
   * are different questions and only the first is what the mode names.
   *
   * <p>So these are written here for one reason: they are what the validated +124.05% figure was
   * measured at, and an entry absent from this table would leave a reader who had switched from
   * Legacy running the Timing rules inside Legacy's 3-tranche, 17-bar-fan environment with
   * nothing on screen to say the figures no longer applied. Three of them equal
   * {@code CONTROL_MANAGEMENT} exactly and the fourth equals what unticking writes -- the fan
   * width's documented pre-fill of 2 rather than the control's 0 -- so unticking the preset is a
   * no-op here rather than a trap.
   */
  TIMING_ENGINE: Object.freeze({
    maxTranches: 1,
    fanExit: false,
    fanFractalWidth: 2,
    chargedContinuation: false,
  }),
});

export const CONTROL_MANAGEMENT = Object.freeze({
  fanExit: false,
  fanReload: false,
  atrTrailingStop: false,
  /**
   * 0 IS THE ENGINE'S OWN "OFF", which is why the field can be emptied rather than unticked.
   *
   * <p>`maxBarsInTrade` is a bar count whose zero means no limit, so there is no separate switch
   * to keep in step with it — a toggle plus a number is two states for one setting and they
   * disagree the moment somebody edits only one of them.
   */
  maxBarsInTrade: 0,

  /**
   * THE TUNING PARAMETERS, and 0 means "leave the engine's own default alone".
   *
   * <p>NOT the engine's values copied in. A copied default is a second declaration of one number
   * that agrees on the day it is written and never again — and worse, it would PIN the value:
   * every request would then carry it explicitly, so a later change to the engine's own default
   * would silently stop reaching this application. Zero is the one value that cannot be a real
   * setting for any of the four (a zero-bar fractal, a retracement of nothing, a zero-period
   * average), which is what lets it mean "unset" without a second flag beside it.
   *
   * <p>So an untouched dialog sends none of them, and a bare CLI run and a bare session request
   * are the same configuration — which is what keeps a published figure reproducible from either.
   */
  fanFractalWidth: 0,
  reloadEma: 0,

  /**
   * HOW CLOSE TO A STRUCTURAL LINE COUNTS AS REACHING IT, in ATRs. 0 is the strict rule.
   *
   * <p>A line is a computed number with four decimal places that price has no obligation to
   * visit, so a rally that stalls three cents under the Top and turns has tested it in every
   * sense a reader cares about and fired nothing. The miss is invisible: no error, no declined
   * intent, just a bar that did not qualify.
   *
   * <p>In ATRs rather than percent because the currency of a near-miss is NOISE, and 0.5% is most
   * of a quiet 1990s session's range and a rounding error against an 8% day in March 2020.
   */
  structuralTolerance: 0,

  /**
   * MAY ONE BAR CLOSE A PREMISE AND OPEN THE NEXT? Off, like every other mechanic here.
   *
   * <p>With it on the engine evaluates exits first and then offers the same bar to the entry
   * rules with the flattened state, so a bar can close a short and open a long. Off because the
   * measurement is SPLIT rather than good or bad -- see the dialog's own hint.
   */
  sameBarEntry: false,

  /**
   * THE THREE LINES AS DYNAMIC LEVELS: recaptured, or held as support. Off, like every mechanic.
   *
   * <p>ADDITIVE rather than a replacement, which is what distinguishes it from the band
   * vocabularies: the equator field and the eight micro-crossing rules are alternatives, and this
   * answers a question neither asks -- has a level been lost and taken back? It is consulted only
   * for a bar the running vocabulary declined, so no existing signal is relabelled.
   */
  crossoverRebound: false,

  /**
   * HOW MANY PREMISES THE BOOK MAY HOLD AT ONCE. ONE, which is the engine as it has always been.
   *
   * <p>Unlike every other number here, 1 is a real setting rather than "unset" -- there is no
   * such thing as a book with no slots. So it is always sent, and the request carries an explicit
   * 1 for the default rather than omitting it: the backend refuses anything outside 1..3, so an
   * omitted value and a 1 would be indistinguishable on the wire and a reader reading a captured
   * request could not tell a single-tranche run from an older client.
   */
  maxTranches: 1,

  /**
   * THE EXIT-WATERMARK CONTINUATION. Off.
   *
   * <p>It is an ENTRY mechanic, which is why it does not sit with the exits: any premise stopped
   * or fanned out arms a watch on the price it was booked out at, and the book retakes the
   * position when price crosses back through that watermark with the trend agreeing.
   *
   * <p>The field keeps its name because it is on the wire. It once required the premise to have
   * opened beyond the Top or below the Bottom; that constraint is gone, and the mechanic now
   * applies across the whole structural field including the neutral channel.
   */
  chargedContinuation: false,

  /**
   * THE CONTINUATION'S TREND FILTER, as an SMA length. 0 means "the engine's own 50".
   *
   * <p>A length rather than a pair, and separate from the macro 50/200 gate: that gate compares
   * two averages and answers a REGIME, this asks whether price is on the right side of one average
   * at the moment the breakout fires.
   */
  continuationTrendSma: 0,

  /**
   * WHICH EXECUTION LAYER trades the signals. LEGACY, which is the frozen state machine and the
   * engine every published figure was measured on.
   *
   * <p>It sits in `management` rather than beside the mandate because it IS a management choice
   * in the only sense that matters here: the mandate picks which signals exist, and this picks
   * what happens to them. Keeping it in one object also means it travels by the same route every
   * other lever does, which is the hop that has eaten seven of them.
   */
  engineMode: ENGINE_MODES[0].value,

  /** How far in favour, in R, before the DIRECT engine moves its stop to the entry. 0 = its 1.0. */
  directBreakevenR: 0,

  /**
   * WHETHER THE SELECTED ENGINE'S PRESET OVERRIDES the levers above it.
   *
   * <p>FALSE in the control, and that is what makes this object still mean what it says. A preset
   * would turn the fan on, the macro gate on and the book up to three tranches -- so a "control"
   * carrying one would be every mechanic switched off and then four of them switched back on by
   * the engine selection, which is the opposite of a baseline. {@link DEFAULT_MANAGEMENT} carries
   * it true, because selecting an engine in the dialog IS asking for that engine's environment.
   */
  enginePreset: false,

  /**
   * THE DECISION COMMITTEE: NULL, meaning unset — not `false`.
   *
   * <p>Every other lever in this object is a boolean because its default is a constant. This
   * one's default is PER MODE: ON for PTB + Timing Engine, OFF everywhere else, resolved by
   * `DynaManRuleConfig.committeeOverrideActive`. Sending `false` from the control would switch
   * it off for the one mode it is the default of, so the control has to be able to say nothing.
   */
  committeeOverride: null,

  /**
   * HOW MANY OF THE COMMITTEE'S FOUR VOTES MUST CARRY. 0 means UNSET, which is the convention
   * every other numeric lever here uses — the backend then applies its own default of 3.
   *
   * <p>Zero cannot be a real setting: a threshold of zero would hold every stop, which is not a
   * committee, it is the exit rule deleted. So it is free to mean "unset" without a second flag
   * beside it, exactly as it does for the time stop and the confirm EMA.
   */
  committeeMinVotes: 0,
});

/**
 * WHAT THE DIALOG OPENS ON: the fan and its reload, the trail and the time stop off.
 *
 * <h2>A UI default is not a model default, and these used to be the same object</h2>
 *
 * They were identical while the dialog opened on the control, and splitting them is what the new
 * default forced. `buildSession()` with no management still yields {@link CONTROL_MANAGEMENT},
 * because a programmatic caller that says nothing must not silently acquire an exit rule. This
 * one is a CHOICE the modal makes on a reader's behalf, which a reader can see and undo.
 *
 * <h2>Why the fan, when the measurement says it costs money</h2>
 *
 * On PTA@SPY the fan cost 32 points of gross, and every published figure is the control. What it
 * buys instead is the thing a session is FOR: the fan is the only one of the four mechanics that
 * DRAWS - a line that steepens to each new swing pivot and then breaks - so a reader watching bar
 * by bar can see an exit being decided rather than read its name afterwards. The reload comes
 * with it because nothing else arms the retracement watch.
 *
 * <p>The control is two clicks away and `isControlManagement` still names it, so reproducing a
 * published figure stays a deliberate act.
 */
export const DEFAULT_MANAGEMENT = Object.freeze({
  ...CONTROL_MANAGEMENT,

  /**
   * THE PRESET IS OFF UNTIL AN ENGINE IS SELECTED, which is what the brief asks for and the only
   * reading that keeps this dialog honest.
   *
   * <p>Selecting an engine -- touching the dropdown, or ticking this switch -- MATERIALISES that
   * engine's preset into the controls below, so the screen and the run agree. The alternative,
   * applying it to the state nobody has touched, would mean a dialog that opens showing "1 tranche,
   * fan width 2" on a run that used 3 and 8: the invisible divergence CLAUDE.md records three times
   * as this repository's worst defect class, arriving by a new route.
   *
   * <p>It would also silently rewrite two measured UI decisions -- the tranche field pre-filled at
   * 1 for cycle isolation, and the fan width pre-filled at 2 because that is the engine's own
   * default and every figure on record uses it -- which is a retune of the dialog wearing a
   * default's clothes. The CLI and the API default it ON instead, because neither has controls to
   * disagree with.
   *
   * <p>So an untouched dialog launches exactly what it displays, and `isControlManagement` stays
   * reachable in the documented two switches.
   */
  enginePreset: false,
  // UNSET, so selecting PTB + Timing gets that mode's documented default without the dialog
  // asserting anything. The switch writes true or false the moment a reader touches it.
  committeeOverride: null,
  committeeMinVotes: 0,
  fanExit: true,
  fanReload: true,
  /*
   * THE FAN'S WIDTH IS PRE-FILLED, and that is the "no silent defaults" rule rather than an
   * exception to it.
   *
   * The objection to a blank field is not that the number 2 is wrong -- it is the engine's own
   * default and the figures on record use it -- but that a blank field resolving to 2 invisibly
   * means a reader cannot tell what their run used. A VALUE IN THE FIELD answers that: it is on
   * screen, it is editable, and it travels on the wire explicitly.
   *
   * <p>`CONTROL_MANAGEMENT` keeps 0, which still means "the engine's own default". So a
   * programmatic `buildSession()` and a bare CLI run remain the same configuration, which is what
   * every reproducible figure in CLAUDE.md rests on -- and the dialog, which has a reader to show
   * things to, is the one place that states the value instead of inheriting it.
   *
   * <p>`validateSession` refuses an EMPTIED field while the fan is on, so the mandatory half of
   * the rule survives a reader clearing it.
   */
  fanFractalWidth: 2,

  /*
   * THE THREE LINES AS DYNAMIC LEVELS, ON since Phase 13 -- and this is a change to what a
   * default-dialog run IS, not a cosmetic one.
   *
   * It is on so the concurrency engine has signals to put in its free slots, which is the
   * measured point of the pair: on PTA@SPY three tranches WITH these rules is +194.75% gross
   * against +3.54% for three tranches without them, because these were the signals a single slot
   * was cannibalising.
   *
   * <p>`CONTROL_MANAGEMENT` keeps it OFF, so the published 186-line PTA baseline stays
   * expressible -- which is the half that matters, since every figure on record was measured
   * without it.
   */
  crossoverRebound: true,
});

/** The suggested limit, which is the best row of the four-point sweep and NOT a promoted default. */
export const SUGGESTED_MAX_BARS = 20;

/**
 * A management block with every field coerced, and the one dependency resolved.
 *
 * <h2>THE RELOAD IMPLIES THE FAN</h2>
 *
 * Nothing but a fan break arms the retracement watch, so a reload without the fan is a lever that
 * silently does nothing. It is resolved by IMPLYING the fan rather than by dropping the reload:
 * dropping it narrows what the caller asked for, implying it grants it, and the same choice is
 * made on the API and on the CLI so all three agree. The dialog additionally disables the control,
 * so this branch is only reachable from a programmatic caller.
 *
 * <h2>An empty, blank or unparseable bar count is 0 — disabled</h2>
 *
 * A text field hands back `''` while it is being edited, and `Number('')` is 0 rather than NaN,
 * which would be the right answer by accident. It is written out so a future `type="number"`
 * returning NaN, or a caller passing `null`, lands on the same place: a limit nobody can read is
 * not a limit. Fractions are floored for the same reason — a bar count of 20.7 does not exist.
 */
/** 1..4, or 0 for unset. See `committeeMinVotes` on CONTROL_MANAGEMENT. */
function voteCountOrUnset(value) {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 4 ? n : 0;
}

export function normaliseManagement(management) {
  const m = management || {};
  const fanReload = m.fanReload === true;
  const raw = Number(m.maxBarsInTrade);
  const bars = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
  return {
    fanExit: fanReload || m.fanExit === true,
    fanReload,
    atrTrailingStop: m.atrTrailingStop === true,
    maxBarsInTrade: bars,
    fanFractalWidth: wholeOrUnset(m.fanFractalWidth),
    reloadEma: wholeOrUnset(m.reloadEma),
    // A MULTIPLE OF ATR, so it is not floored and not capped at 1: two ATRs is a legitimate (if
    // wide) tolerance. Only a negative is dropped, because a negative tolerance would narrow a
    // line into a gap price has to land inside -- the opposite of the word.
    structuralTolerance: positiveOrUnset(m.structuralTolerance),
    sameBarEntry: m.sameBarEntry === true,
    crossoverRebound: m.crossoverRebound === true,
    // CLAMPED, not refused: this comes from a <select> with three options, so a value outside the
    // range is a programmatic caller rather than a reader -- and the engine clamps too. The
    // CONFIG refuses, because a config is written by hand.
    maxTranches: trancheCount(m.maxTranches),
    chargedContinuation: m.chargedContinuation === true,
    // A WHOLE NUMBER OF BARS, so the same coercion the fan width and the confirm EMA take. Zero
    // is "unset" and the engine REFUSES a zero of its own, which is why the two cannot collide:
    // an unset field is omitted from the request rather than sent as 0.
    continuationTrendSma: wholeOrUnset(m.continuationTrendSma),
    // COERCED TO A KNOWN MODE rather than passed through. The control is a two-option select, so
    // anything else is a programmatic caller -- and the backend REFUSES an unknown mode with a
    // 400, which is the right answer there and a poor one to hand a reader here.
    engineMode: ENGINE_MODES.some((e) => e.value === m.engineMode)
      ? m.engineMode : ENGINE_MODES[0].value,
    directBreakevenR: positiveOrUnset(m.directBreakevenR),
    // STRICTLY TRUE, like every other boolean here -- an absent field is the control's `false`
    // rather than the dialog's `true`, so a programmatic `buildSession()` with no management still
    // yields the control.
    enginePreset: m.enginePreset === true,
    // THREE-VALUED, deliberately: true, false, or absent. `=== true` here -- the coercion every
    // other boolean gets -- would turn "unset" into "off" and take PTB+Timing's committee away
    // from a caller who never mentioned it.
    committeeOverride: typeof m.committeeOverride === 'boolean' ? m.committeeOverride : null,
    // 1..4, and anything else is UNSET. Out of range is not clamped: 5 is not a threshold
    // anybody meant to type, and reading it as 4 would run a configuration nobody chose.
    committeeMinVotes: voteCountOrUnset(m.committeeMinVotes),
  };
}

/**
 * The management block with the engine's preset written into the levers this dialog shows.
 *
 * <p>`on` false writes the CONTROL's values for the same four, so the switch is reversible. Every
 * other lever is untouched either way -- a reader's time stop, tolerance or confirm EMA is theirs.
 */
export function applyEnginePreset(management, mode, on) {
  const preset = ENGINE_PRESET_LEVERS[mode] || ENGINE_PRESET_LEVERS.LEGACY;
  const keys = Object.keys(ENGINE_PRESET_LEVERS.LEGACY);
  // THE COMMITTEE LEVER IS CLEARED ON AN ENGINE CHANGE, back to unset.
  //
  // Carrying a TRUE out of PTB+Timing would build a request the backend REFUSES -- the config
  // throws on an off-mode true, deliberately, because the flag widens the retained window and a
  // stray one could move a code-frozen ledger. Clearing restores "unset", which every mode
  // accepts and which resolves to that mode's own default.
  const next = {
    ...management, engineMode: mode, enginePreset: on, committeeOverride: null,
  };
  keys.forEach((k) => { next[k] = on ? preset[k] : CONTROL_MANAGEMENT[k]; });
  // THE FAN'S WIDTH IS THE ONE EXCEPTION on the way back: the dialog pre-fills 2 rather than the
  // control's 0, because a blank field resolving to the engine's own default invisibly is the very
  // thing that pre-fill exists to prevent.
  if (!on) { next.fanFractalWidth = 2; }
  // Unticking the fan clears the reload with it, on this path as on the switch's own -- the screen
  // and the request must not disagree about a flag whose control has just become unavailable.
  if (!next.fanExit) { next.fanReload = false; }
  return next;
}

/** A positive whole number, or 0 for "leave the engine's default alone". */
function wholeOrUnset(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** 1..3, defaulting to 1 — the engine as it has always been. */
function trancheCount(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(3, n);
}

/** Any positive number, unfloored, or 0 for unset. */
function positiveOrUnset(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * A fraction in (0, 1], or 0 for unset.
 *
 * @deprecated the retracement band it coerced is gone -- see {@link RETIRED_TUNING}. Kept because
 *             a fraction-valued lever is the likeliest shape for the next one, and because
 *             deleting a tested coercion to add it back is churn rather than cleanup.
 */
export function fractionOrUnset(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 && n <= 1 ? n : 0;
}

/**
 * True when this session leaves every management MECHANIC at the measured control.
 *
 * <p>The tuning parameters are deliberately not consulted, and that is a claim about the engine
 * rather than a convenience: all four are read on the fan and reload paths ALONE, so with every
 * mechanic off a run carrying them is byte-identical to one that does not. A fractal width set
 * while the fan is off changes nothing, and reporting such a run as "not the control" would be
 * telling a reader their published figure had moved when it had not.
 */
export const isControlManagement = (management) => {
  const m = normaliseManagement(management);
  return !m.fanExit && !m.fanReload && !m.atrTrailingStop && m.maxBarsInTrade === 0
    // THE TWO ENGINE LEVERS DO count here, unlike the tuning parameters above them, and the
    // difference is which code they reach. A fractal width is read on the fan path alone, so with
    // the fan off it cannot move a bar. A structural tolerance changes which bars FIRE A SIGNAL
    // and the same-bar entry changes the lifecycle -- both move a run with every mechanic off, so
    // a run carrying either is not the control and must not be reported as one.
    && m.structuralTolerance === 0 && !m.sameBarEntry && !m.crossoverRebound
    && m.maxTranches === 1 && !m.chargedContinuation
    // AND IT IS THE LEGACY ENGINE. A Direct run shares no exit rule with the control at all, so
    // reporting it as one would tell a reader their published figure had not moved when every
    // mechanic under it had been replaced.
    && m.engineMode === ENGINE_MODES[0].value
    // AND NO PRESET. It overrides four of the levers asserted above, so a preset run is not the
    // control however carefully each of them was set -- and reporting it as one would be reporting
    // a configuration nobody chose under the name of the one every published figure used.
    && !m.enginePreset;
};

/**
 * Is this symbol in the catalogue?
 *
 * <h2>An UNAVAILABLE catalogue does not make every symbol invalid</h2>
 *
 * The list is fetched lazily and a failure is non-fatal by design. Treating "we could not ask"
 * as "the answer is no" would make a network blip look like a typo and block every session —
 * which is a worse failure than letting an unknown ticker through to a backend that will refuse
 * it with its own message.
 *
 * <p>So: a null or empty catalogue validates everything, and the dialog says the check could not
 * be made rather than pretending it passed.
 */
export function isKnownSymbol(symbol, catalogue) {
  const name = typeof symbol === 'string' ? symbol.trim().toUpperCase() : '';
  if (!name) return false;
  const list = Array.isArray(catalogue) ? catalogue : null;
  if (!list || list.length === 0) return true;
  return list.some((s) => typeof s === 'string' && s.trim().toUpperCase() === name);
}

/** Whether this session's clock may run on a timer at all. */
export const isAutoProgression = (session) =>
  !session || session.progression !== PROGRESSION.MANUAL;

/**
 * The frame indices a session covers, inclusive.
 *
 * <p>Resolved against the timeline's FRAMES, not against the chart's dates, because the frame
 * index is what the clock addresses and what every annotation is bounded by. A session whose dates
 * fall between sessions snaps inward — the first frame at or after the start, the last at or
 * before the end — so a window chosen on a weekend is the trading days inside it rather than
 * nothing at all.
 *
 * @returns {{from: number, to: number}} always a valid range over the frames that exist
 */
export function frameBounds(timeline, startDate, endDate) {
  const frames = Array.isArray(timeline?.frames) ? timeline.frames : [];
  const last = frames.length - 1;
  if (last < 0) return { from: 0, to: 0 };

  // String-sliced dates, never parsed: an engine timestamp carries the exchange's own offset and
  // converting through UTC moves a late session onto the next day.
  const dateAt = (i) => sessionDateOf(frames[i].time);

  let from = 0;
  if (startDate) {
    from = frames.findIndex((_, i) => dateAt(i) >= startDate);
    if (from < 0) from = last;                     // the whole run precedes the window
  }

  let to = last;
  if (endDate) {
    to = last;
    while (to > 0 && dateAt(to) > endDate) to -= 1;
  }

  // A reversed or empty window becomes the single bar at its start rather than an error: the
  // modal validates before launching, and a range that survives to here must still produce a
  // transport that can be pressed.
  if (to < from) to = from;
  return { from, to };
}

/**
 * The engine's trades that CLOSED inside the session, up to the playhead.
 *
 * <p>`frameIndex` on a stamped trade is its EXIT frame, so this is the engine's realised book for
 * the session — which is what the human's is, and the only basis on which the two can be
 * subtracted.
 */
export function algoTradesBetween(timeline, fromIndex, toIndex) {
  const stamped = Array.isArray(timeline?.trades) ? timeline.trades : [];
  return stamped
    .filter((s) => s && s.trade && Number.isInteger(s.frameIndex)
      && s.frameIndex >= fromIndex && s.frameIndex <= toIndex)
    .map((s) => s.trade);
}

/**
 * THE ENGINE'S OPEN PREMISE, CLOSED AT THE TERMINATION BAR.
 *
 * <h2>Why this is synthesised in the browser at all</h2>
 *
 * Terminating a session has to flatten BOTH books, or the final snapshot compares a closed human
 * book against an engine still holding a position — and on this rule set a single open premise can
 * be worth more than every closed trade together, so the comparison would be decided by what was
 * excluded from it.
 *
 * <p>The engine cannot be asked: it finished replaying before the operator pressed anything. So
 * the premise is marked out at the bar's close through `accountCosts`, the SAME module that prices
 * the human side, and the trade is stamped `terminated` so nothing can mistake it for an exit the
 * FSM decided. `exitReasonCode` says `SESSION_TERMINATED`, which is not an `ExitReason` the engine
 * can emit — deliberately, so it cannot be confused with one.
 *
 * @param activeTrade the HUD's open-trade view: direction, entryTime, entryPrice
 * @param bar         `{date, close}` at the playhead
 * @param account     the account the run was priced with
 * @param shares      the engine's own share count at this bar; 1 for an unsized run, which is what
 *                    `AccountSimulation` uses and keeps the figure a per-share percentage model
 * @returns a trade-shaped object, or null when nothing was open
 */
export function terminalAlgoTrade(activeTrade, bar, account, shares) {
  if (!activeTrade || !bar || !Number.isFinite(bar.close)) return null;
  const entry = activeTrade.entryPrice;
  if (!Number.isFinite(entry) || entry === 0) return null;

  const direction = activeTrade.direction === 'SHORT' ? 'SHORT' : 'LONG';
  const sign = direction === 'SHORT' ? -1 : 1;
  const size = Number.isFinite(shares) && shares > 0 ? shares : 1;
  const priced = priceTrade({
    direction, entryPrice: entry, exitPrice: bar.close, shares: size, account,
  });

  return {
    direction,
    entryTime: activeTrade.entryTime,
    entryPrice: entry,
    exitTime: bar.date,
    exitPrice: bar.close,
    shares: size,
    // GROSS on the raw prices, as everywhere: `AccountSimulation` never rewrites a trade's
    // percentage, so matching that is what lets the two Gross Return columns be compared.
    pnlPercent: ((bar.close - entry) / entry) * 100 * sign,
    netPnl: priced.net,
    grossPnl: priced.gross,
    commission: priced.commission,
    slippageCost: priced.slippageCost,
    exitReason: 'Session terminated by the operator',
    exitReasonCode: 'SESSION_TERMINATED',
    tradingDays: 1,
    /** Marks this as a MARK-OUT, not an engine decision. Nothing else sets it. */
    terminated: true,
  };
}

/**
 * The engine's share count at a frame, for the terminal mark-out.
 *
 * <p>Zero on an unsized run, which the caller turns into one share rather than refusing: an
 * unsized run's figures are percentages and a one-share fill is exactly how `AccountSimulation`
 * produces them.
 */
export function algoSharesAtFrame(timeline, index) {
  const frame = (timeline?.frames || [])[index];
  const shares = frame?.account?.openShares;
  return Number.isFinite(shares) ? Math.abs(shares) : 0;
}

/**
 * Is this session askable?
 *
 * <p>Returns the sentence to show, or null. Validated in the modal rather than on launch, so the
 * operator is told before a request is made and not after one comes back empty.
 */
export function validateSession({ startDate, endDate, overlays, mandate, management } = {}) {
  if (!startDate || !endDate) return 'Choose both a start and an end date.';
  if (startDate > endDate) return 'The start date is after the end date.';
  if (!MANDATE_VALUES.includes(mandate || DEFAULT_MANDATE)) {
    return 'Choose which engine the algo book trades.';
  }
  const chosen = STRUCTURAL_CATEGORIES.filter((c) => overlays && overlays[c]);
  if (chosen.length === 0) {
    // NOT a hard refusal of the run — the engine would still trade and the human could still
    // trade against price alone. It is refused because a simulator session with no structure on
    // the canvas is almost certainly a mis-set modal rather than a choice, and saying so costs
    // one click to override by ticking the mandate's own overlay.
    return 'Select at least one structural overlay to draw on the canvas.';
  }
  /*
   * THE FAN'S PIVOT WIDTH IS MANDATORY WHILE THE FAN IS ON, and this is the one field in the
   * dialog that refuses to be left empty.
   *
   * Every other number here means "the engine's own default" when blank, which is a visible
   * decision: the placeholder says what that default is. The fan's width is different because it
   * is the ONE parameter that decides what the mechanic the operator has just switched on
   * actually looks for -- and the measured spread across it is enormous (fan exits 85 at width 2
   * against 20 at width 5, gross +2.13% against +40.33% on PTB). A run that silently took 2
   * because nobody typed anything is a run whose headline figure was chosen by a default.
   *
   * <p>Refused in the modal rather than defaulted in the engine, because the engine must keep
   * its default: `buildSession()` from a programmatic caller and a bare CLI run have to stay the
   * same configuration, and that is what every reproducible figure on record rests on.
   */
  const m = normaliseManagement(management);
  if (m.fanExit && !(m.fanFractalWidth > 0)) {
    return 'Set the fan pivot width — with fan line exits on, it is what decides which swings '
      + 'the fan anchors to, and the engine default is not assumed for you.';
  }
  return null;
}

/** A session object, with every field defaulted. */
export function buildSession({
  startDate, endDate, overlays, mandate, progression, symbol, engine, management,
} = {}) {
  return {
    startDate: startDate || null,
    endDate: endDate || null,
    overlays: { ...DEFAULT_OVERLAYS, ...(overlays || {}) },
    mandate: MANDATE_VALUES.includes(mandate) ? mandate : DEFAULT_MANDATE,
    progression: progression === PROGRESSION.MANUAL ? PROGRESSION.MANUAL : DEFAULT_PROGRESSION,
    symbol: symbol || null,
    /**
     * WHETHER THE BARS ARE SENT TO THE ENGINE AT ALL.
     *
     * <p>True for every ordinary symbol — the backend already has that data, so there is nothing
     * to withhold, and an algo column is the whole point of a comparison. The choice exists for
     * an uploaded series, where sending it is the only way to have the engine's opinion and NOT
     * sending it is the only way to keep it private. Defaults to true so a caller that omits the
     * field gets a comparison rather than a silently empty column.
     */
    engine: engine === false ? false : true,
    /**
     * THE TRADE-MANAGEMENT LEVERS, normalised here so every consumer sees one shape.
     *
     * <p>On the SESSION rather than only in the request builder, because a mandate change rebuilds
     * the request from the session — `handleMandateChange` spreads it — so a lever held anywhere
     * else would be silently dropped the first time the operator switched engine. That is the
     * defect this repository has shipped twice: three run paths from one dialog, sending three
     * different configurations.
     */
    management: normaliseManagement(management),
  };
}

/**
 * The bands a session DRAWS, which is not the same as the bands the run produced.
 *
 * <p>Execution and visualisation are independent: the canvas shows every category the operator
 * ticked, and the algo book trades only the mandate. This is the filter that keeps the first half
 * of that true — and it is a filter rather than a fetch, because a band's Top and Bottom are
 * price extremes computed by the engine and a chart that derived its own would be auditing itself.
 */
export function visibleBands(timeBands, overlays) {
  const bands = Array.isArray(timeBands) ? timeBands : [];
  if (!overlays) return bands;
  return bands.filter((b) => b && overlays[b.category]);
}

/** The account's normalised form, so a session's figures and the engine's agree on the levers. */
export const sessionAccount = (account) => normaliseAccount(account);
